import React, { useMemo } from 'react';
import { Event } from '../../types/event.types';
import { Icon, FavoriteButton } from '../../pages/DesignSystem';
import { formatEventDateTime } from '../../utils/timezoneUtils';
import { isEventPast } from '../../utils/eventUtils';
import { optimizeCloudinaryUrl } from '../../services/cloudinary.service';
import './EventCardCompact.css';

interface EventCardCompactProps {
  event: Event;
  seriesCount?: number;
  onClick?: () => void;
  showFavoriteButton?: boolean;
  /** Index dans la liste pour l'animation undo */
  listIndex?: number;
}

const EventCardCompact: React.FC<EventCardCompactProps> = ({
  event,
  seriesCount,
  onClick,
  showFavoriteButton = false,
  listIndex = 0
}) => {
  // Vérifier si l'event est passé
  const isPast = useMemo(() => {
    return event.datetime ? isEventPast(event.datetime) : false;
  }, [event.datetime]);

  // Vérifier si l'event est annulé
  const isCancelled = event.status === 'cancelled';

  // Format datetime pour l'affichage
  const formattedDateTime = useMemo(() => {
    if (!event.datetime) return null;
    return formatEventDateTime(event.datetime, event.timezone);
  }, [event.datetime, event.timezone]);

  return (
    <div
      className={`event-card-compact ${isPast ? 'event-card-compact--past' : ''} ${isCancelled ? 'event-card-compact--cancelled' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Image - only shown if event has an image */}
      {event.image && (
        <div className="event-card-compact__image">
          <img
            src={optimizeCloudinaryUrl(event.image, { width: 200 })}
            alt={event.title}
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className="event-card-compact__content">
        {/* Badge Annulé si l'event est cancelled */}
        {isCancelled && (
          <span className="event-card-compact__cancelled-badge">Cancelled</span>
        )}
        <h3 className="event-card-compact__title">
          {event.title}
        </h3>

        {/* Date */}
        {formattedDateTime && (
          <div className="event-card-compact__meta">
            <Icon name="Calendar" size={12} />
            <span>{formattedDateTime.date}</span>
          </div>
        )}

        {/* Time */}
        {formattedDateTime && (
          <div className="event-card-compact__meta">
            <Icon name="Clock" size={12} />
            <span>{formattedDateTime.time}</span>
          </div>
        )}

        {/* Location */}
        {event.location && (
          <div className="event-card-compact__meta">
            <Icon name="MapPin" size={12} />
            <span className="event-card-compact__location">{event.location}</span>
          </div>
        )}

        {/* Series count badge */}
        {seriesCount && seriesCount > 1 && (
          <div className="event-card-compact__series">
            <Icon name="Repeat" size={12} />
            <span>{seriesCount} dates</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {showFavoriteButton && (
        <div className="event-card-compact__actions">
          <FavoriteButton
            id={event.seriesId || event.id}
            size="sm"
            fullEvent={event}
            listIndex={listIndex}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(EventCardCompact);
